import React, { useState, useEffect, useMemo } from 'react'
import {
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon,
  Loader2Icon,
  UsersIcon,
  FileTextIcon,
  DollarSignIcon,
  ClockIcon,
  SaveIcon,
  LinkIcon,
  DownloadIcon,
  ListChecksIcon,
  BanIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileStatStrip,
  MobileSection,
  mobile,
} from '../components/mobile'

interface QBSyncConfig {
  id?: string
  enabled: boolean
  wc_username: string
  wc_password: string
  company_file_path: string
  app_id?: string
  file_id?: string
  owner_id?: string
  sync_customers: boolean
  sync_invoices: boolean
  sync_payments: boolean
  last_sync_at: string | null
  last_sync_status: string | null
  last_sync_error: string | null
}

interface QueueRow {
  id: string
  entity_type: 'customer' | 'invoice' | 'payment'
  entity_id: string
  action: string
  status: 'pending' | 'in_progress' | 'done' | 'error'
  attempts: number
  last_error: string | null
  queued_at: string
  processed_at: string | null
}

interface MappingRow {
  id: string
  entity_type: string
  local_id: string
  qb_id: string
  qb_edit_sequence: string | null
  local_name: string | null
  qb_name: string | null
  sync_status: string
  synced_at: string
}

interface LogRow {
  id: string
  session_ticket: string | null
  entity_type: string | null
  entity_id: string | null
  request_type: string | null
  status: string
  message: string | null
  started_at: string
  completed_at: string | null
}

interface ExcludedInvoice {
  id: string
  invoice_number: string
  invoice_date: string
  total: number
  balance_due: number
  amount_paid: number
  status: string
  customer?: { company_name?: string }
}

const DEFAULT_CONFIG: QBSyncConfig = {
  enabled: false,
  wc_username: 'rbcfs_sync',
  wc_password: '',
  company_file_path: '',
  sync_customers: true,
  sync_invoices: true,
  sync_payments: true,
  last_sync_at: null,
  last_sync_status: null,
  last_sync_error: null,
}

export function QuickBooksSync() {
  const [activeTab, setActiveTab] = useState<'setup' | 'queue' | 'mappings' | 'logs' | 'excluded'>('setup')
  const [config, setConfig] = useState<QBSyncConfig>(DEFAULT_CONFIG)
  const [queue, setQueue] = useState<QueueRow[]>([])
  const [mappings, setMappings] = useState<MappingRow[]>([])
  const [logs, setLogs] = useState<LogRow[]>([])
  const [excludedInvoices, setExcludedInvoices] = useState<ExcludedInvoice[]>([])
  const [entityLabels, setEntityLabels] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)

  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    await Promise.all([fetchConfig(), fetchQueue(), fetchMappings(), fetchLogs(), fetchExcludedInvoices()])
    setLoading(false)
  }

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase.from('qb_sync_config').select('*').limit(1).maybeSingle()
      if (!error && data) setConfig({ ...DEFAULT_CONFIG, ...data })
    } catch {
      // table may not exist yet
    }
  }

  const fetchQueue = async () => {
    try {
      const { data, error } = await supabase
        .from('qb_sync_queue')
        .select('*')
        .order('queued_at', { ascending: false })
        .limit(200)
      if (!error && data) {
        setQueue(data)
        await resolveEntityLabels(data)
      }
    } catch {
      setQueue([])
    }
  }

  const fetchMappings = async () => {
    try {
      const { data, error } = await supabase
        .from('qb_entity_mappings')
        .select('*')
        .order('synced_at', { ascending: false })
        .limit(200)
      if (!error && data) {
        setMappings(data)
        await resolveEntityLabels(data.map((m) => ({ entity_type: m.entity_type, entity_id: m.local_id })))
      }
    } catch {
      setMappings([])
    }
  }

  const fetchLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('qb_sync_logs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(200)
      if (!error && data) {
        setLogs(data)
        await resolveEntityLabels(data)
      }
    } catch {
      setLogs([])
    }
  }

  const fetchExcludedInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, invoice_date, total, balance_due, amount_paid, status, customer:customers(company_name)')
        .eq('exclude_from_qb', true)
        .order('invoice_date', { ascending: false })
        .limit(200)
      if (!error && data) setExcludedInvoices(data as any)
    } catch {
      setExcludedInvoices([])
    }
  }

  /** Best-effort label resolution for generic entity_type/entity_id rows (queue/mappings/logs). */
  const resolveEntityLabels = async (rows: Array<{ entity_type: string | null; entity_id: string | null }>) => {
    const idsByType: Record<string, Set<string>> = { customer: new Set(), invoice: new Set(), payment: new Set() }
    for (const row of rows) {
      if (row.entity_type && row.entity_id && idsByType[row.entity_type]) {
        idsByType[row.entity_type].add(row.entity_id)
      }
    }
    const next: Record<string, string> = {}
    if (idsByType.customer.size > 0) {
      const { data } = await supabase.from('customers').select('id, company_name').in('id', Array.from(idsByType.customer))
      for (const c of data || []) next[`customer:${c.id}`] = c.company_name || 'Unnamed customer'
    }
    if (idsByType.invoice.size > 0) {
      const { data } = await supabase.from('invoices').select('id, invoice_number').in('id', Array.from(idsByType.invoice))
      for (const inv of data || []) next[`invoice:${inv.id}`] = inv.invoice_number || inv.id
    }
    if (idsByType.payment.size > 0) {
      const { data } = await supabase.from('payments').select('id, amount, reference_number').in('id', Array.from(idsByType.payment))
      for (const p of data || []) next[`payment:${p.id}`] = p.reference_number || `$${Number(p.amount || 0).toFixed(2)} payment`
    }
    setEntityLabels((prev) => ({ ...prev, ...next }))
  }

  const labelFor = (entityType: string | null, entityId: string | null) => {
    if (!entityType || !entityId) return '—'
    return entityLabels[`${entityType}:${entityId}`] || `${entityType} ${entityId.slice(0, 8)}…`
  }

  const saveConfig = async () => {
    try {
      setSaving(true)
      const payload = {
        enabled: config.enabled,
        wc_username: config.wc_username,
        wc_password: config.wc_password,
        company_file_path: config.company_file_path,
        sync_customers: config.sync_customers,
        sync_invoices: config.sync_invoices,
        sync_payments: config.sync_payments,
      }
      if (config.id) {
        const { error } = await supabase.from('qb_sync_config').update(payload).eq('id', config.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('qb_sync_config').insert(payload)
        if (error) throw error
      }
      setMessage({ type: 'success', text: 'QuickBooks Web Connector settings saved.' })
      setTimeout(() => setMessage(null), 3000)
      await fetchConfig()
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to save settings: ' + (err.message || 'Unknown error') })
    } finally {
      setSaving(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'done':
      case 'synced':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />
      case 'error':
        return <XCircleIcon className="w-4 h-4 text-red-600" />
      case 'pending':
      case 'in_progress':
        return <ClockIcon className="w-4 h-4 text-yellow-600" />
      default:
        return <AlertTriangleIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const queueCounts = useMemo(() => {
    const counts = { pending: 0, in_progress: 0, done: 0, error: 0 }
    for (const row of queue) counts[row.status] = (counts[row.status] || 0) + 1
    return counts
  }, [queue])

  const QUEUE_COLUMNS = [
    { id: 'entity', label: 'Entity' },
    { id: 'action', label: 'Action' },
    { id: 'status', label: 'Status' },
    { id: 'attempts', label: 'Attempts' },
    { id: 'error', label: 'Last Error' },
    { id: 'queued_at', label: 'Queued At' },
  ]
  const MAPPINGS_COLUMNS = [
    { id: 'type', label: 'Type' },
    { id: 'local_name', label: 'RBCFS Record' },
    { id: 'qb_id', label: 'QB ID' },
    { id: 'status', label: 'Status' },
    { id: 'synced_at', label: 'Synced At' },
  ]
  const LOGS_COLUMNS = [
    { id: 'time', label: 'Time' },
    { id: 'entity', label: 'Entity' },
    { id: 'request_type', label: 'Request Type' },
    { id: 'status', label: 'Status' },
    { id: 'message', label: 'Message' },
  ]
  const { visibleColumns: queueVisibleColumns, toggleColumn: toggleQueueColumn } = useColumnSelector(QUEUE_COLUMNS, { storageKey: 'qb_queue' })
  const { visibleColumns: mappingsVisibleColumns, toggleColumn: toggleMappingsColumn } = useColumnSelector(MAPPINGS_COLUMNS, { storageKey: 'qb_mappings' })
  const { visibleColumns: logsVisibleColumns, toggleColumn: toggleLogsColumn } = useColumnSelector(LOGS_COLUMNS, { storageKey: 'qb_logs' })

  if (loading) {
    return (
      <MobilePage className="flex justify-center items-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="QuickBooks Sync"
        subtitle={<span className="hidden sm:inline">One-way sync via Web Connector: RBCFS → QuickBooks Desktop</span>}
      />

      {message && (
        <div
          className={`p-3 rounded-xl border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : message.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className={`${mobile.surface} p-4`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${config.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
          <span className="text-sm font-medium text-gray-900">
            {config.enabled ? 'Web Connector Enabled' : 'Web Connector Disabled'}
          </span>
          {config.last_sync_at && (
            <span className="text-xs sm:text-sm text-gray-500 w-full sm:w-auto">
              Last connection {new Date(config.last_sync_at).toLocaleString()} —{' '}
              <span className={config.last_sync_status === 'success' ? 'text-green-600' : 'text-red-600'}>
                {config.last_sync_status}
              </span>
            </span>
          )}
        </div>
        {config.last_sync_error && <p className="text-sm text-red-600 mt-2">{config.last_sync_error}</p>}
      </div>

      <div className={`${mobile.surface} overflow-hidden`}>
        <div className="flex border-b border-gray-100 overflow-x-auto scrollbar-none">
          {([
            { id: 'setup', label: 'Setup' },
            { id: 'queue', label: `Queue (${queueCounts.pending + queueCounts.in_progress})` },
            { id: 'mappings', label: 'Mappings' },
            { id: 'logs', label: 'Logs' },
            { id: 'excluded', label: 'Excluded' },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 min-h-[48px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'setup' && (
        <MobileSection title="Web Connector Setup">
          <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl mb-4">
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900 text-sm">Enable QuickBooks Sync</h3>
              <p className="text-xs text-gray-500 mt-0.5">Allow Web Connector to authenticate and pull queued records</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={mobile.label}>Web Connector Username</label>
              <input
                type="text"
                value={config.wc_username}
                onChange={(e) => setConfig((prev) => ({ ...prev, wc_username: e.target.value }))}
                className={mobile.field}
              />
            </div>
            <div>
              <label className={mobile.label}>Web Connector Password</label>
              <input
                type="password"
                value={config.wc_password}
                onChange={(e) => setConfig((prev) => ({ ...prev, wc_password: e.target.value }))}
                placeholder="Set a password for the .qwc file"
                className={mobile.field}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className={mobile.label}>QuickBooks Company File Path</label>
            <input
              type="text"
              value={config.company_file_path}
              onChange={(e) => setConfig((prev) => ({ ...prev, company_file_path: e.target.value }))}
              placeholder="C:\Users\...\CompanyFile.qbw"
              className={mobile.field}
            />
            <p className="text-xs text-gray-500 mt-1">Full path to the .qbw file on the machine running QB Desktop</p>
          </div>

          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Entity Types to Sync</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: 'sync_customers', label: 'Customers', icon: UsersIcon },
                { key: 'sync_invoices', label: 'Invoices', icon: FileTextIcon },
                { key: 'sync_payments', label: 'Payments', icon: DollarSignIcon },
              ].map((item) => (
                <label key={item.key} className="flex items-center gap-2 p-3 min-h-[48px] bg-gray-50 rounded-xl cursor-pointer active:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={(config as any)[item.key]}
                    onChange={(e) => setConfig((prev) => ({ ...prev, [item.key]: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <item.icon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-900">{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <a href="/api/qbwc.qwc" className={`${mobile.secondaryBtn} w-full sm:w-auto`}>
              <DownloadIcon className="w-4 h-4" />
              Download .qwc File
            </a>
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving}
              className={`${mobile.primaryBtn} w-full sm:w-auto sm:ml-auto`}
            >
              {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <SaveIcon className="w-4 h-4" />}
              Save Settings
            </button>
          </div>

          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 space-y-1">
            <p className="font-medium text-gray-900">Setup steps:</p>
            <p>1. Save your Web Connector username/password and company file path above.</p>
            <p>2. Download the .qwc file and import it into QuickBooks Web Connector on the machine running QB Desktop.</p>
            <p>3. Enter the same username/password when QBWC prompts for credentials.</p>
            <p>4. Leave Web Connector running — it polls <code className="bg-gray-100 px-1 rounded">/api/qbwc</code> on a timer and pushes queued records one-way into QuickBooks.</p>
          </div>
        </MobileSection>
      )}

      {activeTab === 'queue' && (
        <div className="space-y-3">
          <div className="lg:hidden">
            <MobileStatStrip
              stats={[
                { key: 'pending', label: 'Pending', value: String(queueCounts.pending || 0), tone: 'amber' },
                { key: 'progress', label: 'In progress', value: String(queueCounts.in_progress || 0), tone: 'blue' },
                { key: 'done', label: 'Done', value: String(queueCounts.done || 0), tone: 'green' },
                { key: 'error', label: 'Error', value: String(queueCounts.error || 0), tone: 'red' },
              ]}
            />
          </div>
          <div className="hidden lg:grid grid-cols-4 gap-4">
            {([
              { key: 'pending', label: 'Pending', color: 'text-yellow-600 bg-yellow-50' },
              { key: 'in_progress', label: 'In Progress', color: 'text-blue-600 bg-blue-50' },
              { key: 'done', label: 'Done', color: 'text-green-600 bg-green-50' },
              { key: 'error', label: 'Error', color: 'text-red-600 bg-red-50' },
            ] as const).map((c) => (
              <div key={c.key} className="bg-white p-4 rounded-lg border border-gray-200">
                <p className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${c.color}`}>{c.label}</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{queueCounts[c.key] || 0}</p>
              </div>
            ))}
          </div>

          <p className={mobile.sectionLabel}>Sync queue</p>
          <div className="native-mobile-list space-y-2">
            {queue.length === 0 ? (
              <MobileEmptyState
                icon={<ListChecksIcon className="w-12 h-12" />}
                title="Queue is empty"
                description="Items waiting for the QuickBooks Web Connector appear here."
              />
            ) : (
              queue.map((row) => (
                <MobileListRow
                  key={row.id}
                  title={labelFor(row.entity_type, row.entity_id)}
                  badges={
                    <>
                      <span className={`${mobile.badge} bg-blue-100 text-blue-800 capitalize`}>{row.entity_type}</span>
                      <span className={`${mobile.badge} bg-gray-100 text-gray-700 capitalize`}>{row.status}</span>
                    </>
                  }
                  meta={
                    <>
                      <div className="capitalize">{row.action} · {row.attempts} attempt{row.attempts !== 1 ? 's' : ''}</div>
                      <div>{new Date(row.queued_at).toLocaleString()}</div>
                      {row.last_error && <div className="text-red-600 truncate">{row.last_error}</div>}
                    </>
                  }
                  chevron={false}
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ListChecksIcon className="w-5 h-5" /> Sync Queue
                </h2>
                <p className="text-sm text-gray-500">Items waiting for the QuickBooks Web Connector</p>
              </div>
              <ColumnSelector columns={QUEUE_COLUMNS} visibleColumns={queueVisibleColumns} onToggle={toggleQueueColumn} />
            </div>
            {queue.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">Queue is empty.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {queueVisibleColumns.has('entity') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>}
                      {queueVisibleColumns.has('action') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>}
                      {queueVisibleColumns.has('status') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                      {queueVisibleColumns.has('attempts') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>}
                      {queueVisibleColumns.has('error') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Error</th>}
                      {queueVisibleColumns.has('queued_at') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Queued At</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {queue.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        {queueVisibleColumns.has('entity') && (
                          <td className="px-6 py-3 text-sm text-gray-900">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize mr-2">
                              {row.entity_type}
                            </span>
                            {labelFor(row.entity_type, row.entity_id)}
                          </td>
                        )}
                        {queueVisibleColumns.has('action') && <td className="px-6 py-3 text-sm text-gray-500 capitalize">{row.action}</td>}
                        {queueVisibleColumns.has('status') && (
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center gap-1 text-sm">{getStatusIcon(row.status)} {row.status}</span>
                          </td>
                        )}
                        {queueVisibleColumns.has('attempts') && <td className="px-6 py-3 text-sm text-gray-900">{row.attempts}</td>}
                        {queueVisibleColumns.has('error') && (
                          <td className="px-6 py-3 text-xs text-red-600 max-w-xs truncate">{row.last_error || '—'}</td>
                        )}
                        {queueVisibleColumns.has('queued_at') && (
                          <td className="px-6 py-3 text-sm text-gray-500">{new Date(row.queued_at).toLocaleString()}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'mappings' && (
        <div className="space-y-3">
          <p className={mobile.sectionLabel}>Entity mappings · {mappings.length}</p>
          <div className="native-mobile-list space-y-2">
            {mappings.length === 0 ? (
              <MobileEmptyState
                icon={<LinkIcon className="w-12 h-12" />}
                title="No mappings yet"
                description="Records appear here once the Web Connector syncs them."
              />
            ) : (
              mappings.map((m) => (
                <MobileListRow
                  key={m.id}
                  title={m.local_name || labelFor(m.entity_type, m.local_id)}
                  badges={
                    <>
                      <span className={`${mobile.badge} bg-blue-100 text-blue-800 capitalize`}>{m.entity_type}</span>
                      <span className={`${mobile.badge} bg-gray-100 text-gray-700 capitalize`}>{m.sync_status}</span>
                    </>
                  }
                  meta={
                    <>
                      <div className="font-mono text-[11px] truncate">QB {m.qb_id}</div>
                      <div>{new Date(m.synced_at).toLocaleString()}</div>
                    </>
                  }
                  chevron={false}
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" /> Entity Mappings
                </h2>
                <p className="text-sm text-gray-500">{mappings.length} synced records</p>
              </div>
              <ColumnSelector columns={MAPPINGS_COLUMNS} visibleColumns={mappingsVisibleColumns} onToggle={toggleMappingsColumn} />
            </div>
            {mappings.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <LinkIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No mappings yet — records appear here once the Web Connector syncs them.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {mappingsVisibleColumns.has('type') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>}
                      {mappingsVisibleColumns.has('local_name') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RBCFS Record</th>}
                      {mappingsVisibleColumns.has('qb_id') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">QB List/Txn ID</th>}
                      {mappingsVisibleColumns.has('status') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                      {mappingsVisibleColumns.has('synced_at') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Synced At</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {mappings.map((m) => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        {mappingsVisibleColumns.has('type') && (
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                              {m.entity_type}
                            </span>
                          </td>
                        )}
                        {mappingsVisibleColumns.has('local_name') && (
                          <td className="px-6 py-3 text-sm text-gray-900">{m.local_name || labelFor(m.entity_type, m.local_id)}</td>
                        )}
                        {mappingsVisibleColumns.has('qb_id') && <td className="px-6 py-3 text-xs text-gray-500 font-mono">{m.qb_id}</td>}
                        {mappingsVisibleColumns.has('status') && (
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center gap-1 text-sm">{getStatusIcon(m.sync_status)} {m.sync_status}</span>
                          </td>
                        )}
                        {mappingsVisibleColumns.has('synced_at') && (
                          <td className="px-6 py-3 text-sm text-gray-500">{new Date(m.synced_at).toLocaleString()}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-3">
          <p className={mobile.sectionLabel}>Sync history</p>
          <div className="native-mobile-list space-y-2">
            {logs.length === 0 ? (
              <MobileEmptyState
                icon={<ClockIcon className="w-12 h-12" />}
                title="No sync history yet"
              />
            ) : (
              logs.map((log) => (
                <MobileListRow
                  key={log.id}
                  title={labelFor(log.entity_type, log.entity_id)}
                  badges={
                    <span className={`${mobile.badge} bg-gray-100 text-gray-700 capitalize`}>{log.status}</span>
                  }
                  meta={
                    <>
                      <div>{new Date(log.started_at).toLocaleString()}</div>
                      <div>{log.request_type || '—'}</div>
                      {log.message && <div className="truncate">{log.message}</div>}
                    </>
                  }
                  chevron={false}
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Sync History</h2>
              <ColumnSelector columns={LOGS_COLUMNS} visibleColumns={logsVisibleColumns} onToggle={toggleLogsColumn} />
            </div>
            {logs.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No sync history yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {logsVisibleColumns.has('time') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>}
                      {logsVisibleColumns.has('entity') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>}
                      {logsVisibleColumns.has('request_type') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request Type</th>}
                      {logsVisibleColumns.has('status') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                      {logsVisibleColumns.has('message') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        {logsVisibleColumns.has('time') && (
                          <td className="px-6 py-3 text-sm text-gray-900">{new Date(log.started_at).toLocaleString()}</td>
                        )}
                        {logsVisibleColumns.has('entity') && (
                          <td className="px-6 py-3 text-sm text-gray-500 capitalize">{labelFor(log.entity_type, log.entity_id)}</td>
                        )}
                        {logsVisibleColumns.has('request_type') && <td className="px-6 py-3 text-sm text-gray-500">{log.request_type || '—'}</td>}
                        {logsVisibleColumns.has('status') && (
                          <td className="px-6 py-3">
                            <span className="inline-flex items-center gap-1 text-sm">{getStatusIcon(log.status)} {log.status}</span>
                          </td>
                        )}
                        {logsVisibleColumns.has('message') && (
                          <td className="px-6 py-3 text-xs text-gray-600 max-w-xs truncate">{log.message || '—'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'excluded' && (
        <div className="space-y-3">
          <p className={mobile.sectionLabel}>Excluded invoices</p>
          <p className="text-xs text-gray-500 px-1">
            Not yet queued for QuickBooks. Queued automatically when the first payment is recorded with QB sync checked.
          </p>
          <div className="native-mobile-list space-y-2">
            {excludedInvoices.length === 0 ? (
              <MobileEmptyState
                icon={<BanIcon className="w-12 h-12" />}
                title="No excluded invoices"
              />
            ) : (
              excludedInvoices.map((inv) => (
                <MobileListRow
                  key={inv.id}
                  title={inv.invoice_number}
                  subtitle={inv.customer?.company_name || 'Unknown'}
                  badges={
                    <span className={`${mobile.badge} bg-gray-100 text-gray-700 capitalize`}>{inv.status}</span>
                  }
                  meta={<div>{inv.invoice_date}</div>}
                  trailing={
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900 tabular-nums">
                        ${Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 tabular-nums">
                        Due ${Number(inv.balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  }
                  chevron={false}
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BanIcon className="w-5 h-5" /> Excluded Invoices
              </h2>
              <p className="text-sm text-gray-500">
                Invoices not yet queued for QuickBooks. An invoice is queued automatically once its first payment is
                recorded with the &quot;QB&quot; checkbox checked.
              </p>
            </div>
            {excludedInvoices.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">No excluded invoices.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {excludedInvoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{inv.invoice_number}</td>
                        <td className="px-6 py-3 text-sm text-gray-900">{inv.customer?.company_name || 'Unknown'}</td>
                        <td className="px-6 py-3 text-sm text-gray-500">{inv.invoice_date}</td>
                        <td className="px-6 py-3 text-sm text-gray-500 capitalize">{inv.status}</td>
                        <td className="px-6 py-3 text-sm text-gray-900">${Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="px-6 py-3 text-sm text-gray-900">${Number(inv.balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </MobilePage>
  )
}
